import React from "react";
import { Modal, Table } from "antd";
import styled from "@emotion/styled";
import COLORS from "common/Colors";
import { useSelector } from "react-redux";
import { getCountryTimezoneList } from "redux/selectors";
import moment from "moment-timezone";
import { getValidTimezone } from "utils/Date";
import { getTimeZoneByCountry } from "common/Constants";

const StyledModal = styled(Modal)`
  width: 600px !important;

  .ant-modal-header {
    text-transform: uppercase;
    max-width: 95%;
  }
`;
const StyleLocation = styled.div`
  a {
    font-size: 12px;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-content {
    overflow-x: auto;
    max-height: 500px;
    white-space: nowrap;
  }
  .ant-table-cell {
    padding: 10px;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.SECONDARY_BLACK};
  }
  .non-white {
    background: #f9f9f9;
  }
  .normal-column {
    white-space: normal;
  }
`;

// ---- Reverse-geocoding helpers (shared cache + in-flight dedupe) ----
const geoCache = new Map();
const geoInFlight = new Map();

const fetchAddress = (lat, lng) => {
  const key = `${lat},${lng}`;

  if (geoCache.has(key)) return Promise.resolve(geoCache.get(key));
  if (geoInFlight.has(key)) return geoInFlight.get(key);

  const promise = fetch(
    `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
    { headers: { "Accept-Language": "en" } }
  )
    .then((r) => r.json())
    .then((d) => {
      const a = d?.address;
      const label =
        a?.office ||
        a?.building ||
        a?.amenity ||
        a?.road ||
        a?.suburb ||
        a?.city ||
        d?.display_name?.split(",").slice(0, 2).join(", ") ||
        key;
      geoCache.set(key, label);
      geoInFlight.delete(key);
      return label;
    })
    .catch(() => {
      geoCache.set(key, key);
      geoInFlight.delete(key);
      return key;
    });

  geoInFlight.set(key, promise);
  return promise;
};

const useGeoAddress = (lat, lng) => {
  const key = lat && lng ? `${lat},${lng}` : null;

  const [address, setAddress] = React.useState(() =>
    key ? geoCache.get(key) ?? null : null
  );
  const [loading, setLoading] = React.useState(() =>
    key ? !geoCache.has(key) : false
  );

  React.useEffect(() => {
    if (!key) return;

    if (geoCache.has(key)) {
      setAddress(geoCache.get(key));
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetchAddress(lat, lng).then((label) => {
      if (!cancelled) {
        setAddress(label);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { address, loading };
};

// lat alone renders a plain "Lat: <value>" label; the map link / reverse
// geocoded address only appear once both lat and lng are present.
const LocationCell = React.memo(({ lat, lng, locationName, locationId }) => {
  const hasLat = lat != null && lat !== "";
  const hasLng = lng != null && lng !== "";
  const hasCoords = hasLat && hasLng;

  const { address, loading } = useGeoAddress(
    hasCoords ? lat : null,
    hasCoords ? lng : null
  );

  if (!hasLat) return <span>-</span>;

  if (!hasCoords) {
    // Only latitude is available (longitude missing from the API response).
    return (
      <span
        style={{ fontSize: "12px", color: "#666" }}
        title="Longitude not available"
      >
        Lat: {lat}
      </span>
    );
  }

  if (loading)
    return <span style={{ color: "#888", fontSize: 12 }}>Resolving…</span>;

  const mapUrl = `https://maps.google.com/maps/?q=${lat},${lng}`;
  return (
    <StyleLocation className="location">
      <a href={mapUrl} target="_blank" rel="noreferrer">
        {String(locationId) === "6" ? "Client Location" : "Office"}
      </a>
      <br />
      <span style={{ fontSize: "10px", color: "#ccc" }}>
        {lat}, {lng}
      </span>
    </StyleLocation>
  );
});

function LocationBasedAttendanceList({
  displayModal,
  toggleModal,
  title,
  list,
  reportType,
  userInfo,
  userList,
  country, // country selected in the parent (optional)
}) {
  const CountryTimezone = useSelector(getCountryTimezoneList);
  const finduser = userList?.find((item) => item?.userid === userInfo?.userid);

  // Look up a timezone for a country name; returns undefined if missing/invalid.
  const timezoneForCountry = (c) => {
    if (!c) return undefined;

    const tz = CountryTimezone?.find((item) => item?.country === c)?.Timezone;

    // 1. Stored value, if it is already a real IANA zone (e.g. "Africa/Lusaka")
    if (tz && moment.tz.zone(tz)) return tz;

    // 2. Country-name map from Constants ("Zambia" -> "Africa/Lusaka")
    const mapped = getTimeZoneByCountry(c);
    if (mapped && moment.tz.zone(mapped)) return mapped;

    // 3. Existing helper, as a last resort
    const valid = tz ? getValidTimezone(tz) : undefined;
    return valid && moment.tz.zone(valid) ? valid : undefined;
  };

  // Priority: the record's own country -> country selected in the parent
  // -> logged-in user's managing office -> browser timezone.
  // Never undefined (an undefined arg makes .tz() act as a getter).
  const resolveTimezone = (record) =>
    timezoneForCountry(record?.country) ||
    timezoneForCountry(country) ||
    timezoneForCountry(finduser?.managingoffice?.[0]) ||
    moment.tz.guess();

  // API sends check-in time in UTC ("05:37:16"); convert to local time.
  const formatCheckInTime = (text, record) => {
    if (!text) return "-";
    const parsed = moment.utc(text, "HH:mm:ss");
    if (!parsed.isValid()) return "-";
    return parsed.tz(resolveTimezone(record)).format("hh:mm A");
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "fullname",
    },
    {
      title: "Check-In Time",
      dataIndex: "checkintime",
      key: "checkintime",
      align: "center",
      render: (text, record) => formatCheckInTime(text, record),
    },
    {
      title: <div>Location</div>,
      dataIndex: "location",
      key: "loginLatitude",
      className: "normal-column",
      align: "center",
      render: (_, record) => (
        <LocationCell
          lat={record.loginlatitude}
          lng={record.loginlongitude}
          locationName={record.baselocationname}
          locationId={record.loginlocationid}
        />
      ),
    },
  ];

  return (
    <>
      <StyledModal
        title={title}
        visible={displayModal}
        onCancel={toggleModal}
        footer={null}
        destroyOnClose
      >
        <StyledTable
          dataSource={list}
          columns={columns}
          rowClassName={(record, index) => (index % 2 === 0 ? "" : "non-white")}
          rowKey={(record) =>
            record.userid || record.userId || record.user_id || record.fullname
          }
          pagination={false}
        />
      </StyledModal>
    </>
  );
}

export default LocationBasedAttendanceList;